import type { StoreAuditSummaryRangeDayModel } from "models";
import { isAuditDayInProgress } from "./auditDayProgress";
import { getAuditDataAvailability } from "./auditMissingState";

export type AuditListFilterKey = "all" | "need_data" | "deficit" | "surplus" | "matched";
export type AuditListDateStatus = Exclude<AuditListFilterKey, "all"> | "unavailable";

interface AuditListFilterOptions {
  hasPosType: boolean;
  isLottoAuthorized: boolean;
}

export interface AuditListFilterOption {
  value: AuditListFilterKey;
  label: string;
  count: number;
}

export const getAuditListDateStatus = (
  day: StoreAuditSummaryRangeDayModel,
  options: AuditListFilterOptions
): AuditListDateStatus => {
  const totals = day.auditTotals;

  if (isAuditDayInProgress(day.reportDate)) {
    return "unavailable";
  }

  const availability = getAuditDataAvailability(day, options);

  if (availability.hasMissingSources) {
    return "need_data";
  }

  if (!availability.canShowOverallVariance) {
    return "unavailable";
  }

  const totalVariance = totals?.reconciliation_variance?.total_variance;

  if (totalVariance == null) {
    return "unavailable";
  }

  if (totalVariance < 0) {
    return "deficit";
  }

  if (totalVariance > 0) {
    return "surplus";
  }

  return "matched";
};

export const buildAuditListFilterOptions = (
  days: StoreAuditSummaryRangeDayModel[],
  options: AuditListFilterOptions
): AuditListFilterOption[] => {
  const counts: Record<AuditListDateStatus, number> = {
    need_data: 0,
    deficit: 0,
    surplus: 0,
    matched: 0,
    unavailable: 0,
  };

  days.forEach((day) => {
    counts[getAuditListDateStatus(day, options)] += 1;
  });

  const filterOptions: AuditListFilterOption[] = [
    {
      value: "all",
      label: `All (${days.length})`,
      count: days.length,
    },
    {
      value: "need_data",
      label: `${counts.need_data} need data`,
      count: counts.need_data,
    },
    {
      value: "deficit",
      label: `${counts.deficit} ${counts.deficit === 1 ? "deficit" : "deficits"}`,
      count: counts.deficit,
    },
    {
      value: "surplus",
      label: `${counts.surplus} ${counts.surplus === 1 ? "surplus" : "surpluses"}`,
      count: counts.surplus,
    },
    {
      value: "matched",
      label: `${counts.matched} matched`,
      count: counts.matched,
    },
  ];

  return filterOptions.filter((item) => item.value === "all" || item.count > 0);
};

export const filterAuditListDays = (
  days: StoreAuditSummaryRangeDayModel[],
  activeFilter: AuditListFilterKey,
  options: AuditListFilterOptions
) => {
  if (activeFilter === "all") {
    return days;
  }

  return days.filter((day) => getAuditListDateStatus(day, options) === activeFilter);
};
